import React, { useEffect, useState, useCallback } from "react";
import { Table, DatePicker, Spin, Card, Row, Col, Statistic, Typography } from "antd";
import {
    DollarCircleOutlined,
    RiseOutlined,
    CalculatorOutlined,
    LineChartOutlined
} from "@ant-design/icons";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import { ProfitReportData } from "../../types/report";
import dayjs from "dayjs";
import ExcelExportButton from "./ExcelExportButton";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Add some custom styles
const tableStyles = `
    .even-row {
        background-color: #fafafa;
    }
    .odd-row {
        background-color: #ffffff;
    }
    .even-row:hover, .odd-row:hover {
        background-color: #e6f7ff !important;
    }
    .ant-table-thead > tr > th {
        background: linear-gradient(135deg, #232326ff 0%, #111011ff 100%) !important;
        color: white !important;
        font-weight: bold !important;
        border-bottom: none !important;
    }
    .ant-table-tbody > tr > td {
        border-bottom: 1px solid #f0f0f0;
    }
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = tableStyles;
    document.head.appendChild(styleSheet);
}

const ProfitReport: React.FC = () => {
    const { fetchProfitReport } = useReportApi();
    const { t } = useTranslation();
    const [data, setData] = useState<ProfitReportData[]>([]);
    const [totalMetrics, setTotalMetrics] = useState<any>(null);
    const [breakdown, setBreakdown] = useState<any>(null);
    const [topProfitableItems, setTopProfitableItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);

    const fetchData = useCallback(async () => {
        if (!dateRange) return;

        setLoading(true);
        try {
            const result = await fetchProfitReport({
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            });

            setData(result.profitByCurrency || []);
            setTotalMetrics(result.totalMetrics || {});
            setBreakdown(result.breakdown || {});
            setTopProfitableItems(result.topProfitableItems || []);
        } catch (error) {
            // Error handled by API layer
            setData([]);
            setTotalMetrics({});
            setBreakdown({});
            setTopProfitableItems([]);
        } finally {
            setLoading(false);
        }
    }, [dateRange, fetchProfitReport]);

    useEffect(() => {
        fetchData();
    }, [dateRange]); const columns = [
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            width: 120,
            render: (value: string) => (
                <Text strong style={{
                    color: '#1890ff',
                    backgroundColor: '#f0f8ff',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    fontSize: '13px',
                    letterSpacing: '0.5px'
                }}>
                    {value || 'USD'}
                </Text>
            ),
        },
        {
            title: t('total_revenue'),
            dataIndex: 'revenue',
            key: 'revenue',
            width: 140,
            align: 'right' as const,
            render: (value: number) => (
                <Text style={{
                    color: '#52c41a',
                    fontWeight: '600',
                    fontSize: '14px'
                }}>
                    {(value || 0).toLocaleString()}
                </Text>
            ),
        },
        {
            title: t('total_cost'),
            dataIndex: 'costs',
            key: 'costs',
            width: 130,
            align: 'right' as const,
            render: (costs: any) => {
                // Handle both object and number formats
                const totalCost = typeof costs === 'object' ? costs.total || 0 : costs || 0;
                return (
                    <Text style={{
                        color: '#ff4d4f',
                        fontWeight: '600',
                        fontSize: '14px'
                    }}>
                        {totalCost.toLocaleString()}
                    </Text>
                );
            },
        },
        {
            title: t('gross_profit'),
            dataIndex: 'grossProfit',
            key: 'grossProfit',
            width: 130,
            align: 'right' as const,
            render: (value: number) => (
                <Text strong style={{
                    color: (value || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                    fontSize: '14px'
                }}>
                    {(value || 0).toLocaleString()}
                </Text>
            ),
        },
        {
            title: t('net_profit'),
            dataIndex: 'netProfit',
            key: 'netProfit',
            width: 130,
            align: 'right' as const,
            render: (value: number) => (
                <Text strong style={{
                    color: (value || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                    fontSize: '14px'
                }}>
                    {(value || 0).toLocaleString()}
                </Text>
            ),
        },
        {
            title: t('profit_margin'),
            dataIndex: 'profitMargin',
            key: 'profitMargin',
            width: 130,
            align: 'right' as const,
            render: (value: number) => (
                <Text strong style={{
                    color: (value || 0) >= 0 ? '#1890ff' : '#ff4d4f',
                    fontSize: '14px'
                }}>
                    {(value || 0).toFixed(2)}%
                </Text>
            ),
        },
        {
            title: t('transactions'),
            dataIndex: 'transactions',
            key: 'transactions',
            width: 120,
            align: 'center' as const,
            render: (transactions: any) => {
                // Calculate total transactions from the transactions object
                let transactionCount = 0;

                if (transactions && typeof transactions === 'object') {
                    // Sum all transaction types
                    transactionCount = (transactions.sales || 0) +
                        (transactions.purchases || 0) +
                        (transactions.expenses || 0) +
                        (transactions.returns || 0);
                } else if (typeof transactions === 'number') {
                    transactionCount = transactions;
                }

                return (
                    <Text style={{
                        color: '#722ed1',
                        backgroundColor: '#f9f0ff',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '13px'
                    }}>
                        {transactionCount.toLocaleString()}
                    </Text>
                );
            },
        },
    ];

    const handleDateRangeChange = (dates: any) => {
        setDateRange(dates);
    };

    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            {/* Header Section */}
            <div style={{
                marginBottom: '24px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
            }}>
                <Title level={2} style={{ margin: 0, color: 'white' }}>
                    📊 {t('profit_report')}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                    {dateRange && `${dateRange[0].format('MMM DD, YYYY')} - ${dateRange[1].format('MMM DD, YYYY')}`}
                </Text>
            </div>

            {/* Date Range Picker */}
            <Card style={{
                marginBottom: '24px',
                textAlign: 'center',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
                <div style={{ padding: '10px 0' }}>
                    <Text strong style={{ marginRight: '16px', fontSize: '16px', color: '#1890ff' }}>
                        📅
                    </Text>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        format="YYYY-MM-DD"
                        size="large"
                        style={{
                            width: '320px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    />
                    <span style={{ marginInlineStart: 16, display: 'inline-block' }}>
                        <ExcelExportButton
                            fileName="profit-report"
                            sheetName={t('profit_report') as string}
                            columns={[
                                { header: t('currency') as string, key: 'currency', width: 12 },
                                { header: (t('revenue') as string) || 'Revenue', key: 'revenue', width: 16 },
                                { header: t('purchases') as string, key: (row: any) => row.costs?.purchases ?? 0, width: 16 },
                                { header: t('expenses') as string, key: (row: any) => row.costs?.expenses ?? 0, width: 16 },
                                { header: t('supplier_payments') as string, key: (row: any) => row.costs?.supplierPayments ?? 0, width: 18 },
                                { header: t('returns') as string, key: (row: any) => row.costs?.returns ?? 0, width: 16 },
                                { header: (t('total_costs') as string) || 'Total costs', key: (row: any) => row.costs?.total ?? 0, width: 16 },
                                { header: (t('gross_profit') as string) || 'Gross profit', key: 'grossProfit', width: 16 },
                                { header: (t('net_profit') as string) || 'Net profit', key: 'netProfit', width: 16 },
                                { header: (t('profit_margin') as string) || 'Margin %', key: 'profitMargin', width: 14 },
                            ]}
                            rows={data}
                            title={[
                                t('profit_report') as string,
                                dateRange
                                    ? `${dateRange[0].format('YYYY-MM-DD')} → ${dateRange[1].format('YYYY-MM-DD')}`
                                    : '',
                            ]}
                        />
                    </span>
                </div>
            </Card>

            {/* Additional Metrics */}
            {totalMetrics && totalMetrics.byCurrency && Object.keys(totalMetrics.byCurrency).length > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12}>
                        <Card
                            title={<Text style={{ color: '#722ed1', fontWeight: 'bold' }}>
                                📋 {t('transaction_breakdown')}
                            </Text>}
                            style={{
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(114, 46, 209, 0.1)'
                            }}
                        >
                            {breakdown && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px',
                                    padding: '8px'
                                }}>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f6ffed',
                                        borderRadius: '8px',
                                        border: '1px solid #b7eb8f'
                                    }}>
                                        <Text style={{ color: '#52c41a', fontSize: '12px' }}>💰 {t('sales')}</Text>
                                        <br />
                                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                                            {breakdown.salesCount || 0}
                                        </Text>
                                    </div>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#fff2e8',
                                        borderRadius: '8px',
                                        border: '1px solid #ffd591'
                                    }}>
                                        <Text style={{ color: '#fa8c16', fontSize: '12px' }}>🛒 {t('purchases')}</Text>
                                        <br />
                                        <Text strong style={{ fontSize: '16px', color: '#fa8c16' }}>
                                            {breakdown.purchaseCount || 0}
                                        </Text>
                                    </div>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#fff1f0',
                                        borderRadius: '8px',
                                        border: '1px solid #ffa39e'
                                    }}>
                                        <Text style={{ color: '#ff4d4f', fontSize: '12px' }}>💸 {t('expenses')}</Text>
                                        <br />
                                        <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                                            {breakdown.expenseCount || 0}
                                        </Text>
                                    </div>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f9f0ff',
                                        borderRadius: '8px',
                                        border: '1px solid #d3adf7'
                                    }}>
                                        <Text style={{ color: '#722ed1', fontSize: '12px' }}>↩️ {t('returns')}</Text>
                                        <br />
                                        <Text strong style={{ fontSize: '16px', color: '#722ed1' }}>
                                            {breakdown.returnCount || 0}
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Currency Summary Cards */}
            {totalMetrics && totalMetrics.byCurrency && Object.keys(totalMetrics.byCurrency).length > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col span={24}>
                        <Card
                            title={<Text style={{ fontSize: '18px', color: '#722ed1', fontWeight: 'bold' }}>
                                🌍 {t('currency_summary')}
                            </Text>}
                            style={{
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(114, 46, 209, 0.1)'
                            }}
                        >
                            <Row gutter={[16, 16]}>
                                {Object.entries(totalMetrics.byCurrency).map(([currency, data]: [string, any]) => (
                                    <Col xs={24} sm={12} lg={6} key={currency}>
                                        <Card
                                            size="small"
                                            style={{
                                                borderRadius: '8px',
                                                border: `2px solid ${data.netProfit >= 0 ? '#52c41a' : '#ff4d4f'}`,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <div style={{ textAlign: 'center', padding: '8px' }}>
                                                <Text strong style={{
                                                    fontSize: '16px',
                                                    color: '#1890ff',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px'
                                                }}>
                                                    {currency}
                                                </Text>

                                                <div style={{ marginTop: '12px' }}>
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                            📈 {t('revenue')}
                                                        </Text>
                                                        <br />
                                                        <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
                                                            {data.revenue?.toLocaleString() || '0'}
                                                        </Text>
                                                    </div>

                                                    <div style={{ marginBottom: '8px' }}>
                                                        <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                            💰 {t('net_profit')}
                                                        </Text>
                                                        <br />
                                                        <Text strong style={{
                                                            color: data.netProfit >= 0 ? '#52c41a' : '#ff4d4f',
                                                            fontSize: '16px'
                                                        }}>
                                                            {data.netProfit?.toLocaleString() || '0'}
                                                        </Text>
                                                    </div>

                                                    <div>
                                                        <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                            📊 {t('margin')}
                                                        </Text>
                                                        <br />
                                                        <Text strong style={{
                                                            color: data.profitMargin >= 0 ? '#1890ff' : '#ff4d4f',
                                                            fontSize: '14px'
                                                        }}>
                                                            {data.profitMargin?.toFixed(2) || '0.00'}%
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Top Profitable Items */}
            {topProfitableItems && topProfitableItems.length > 0 && (
                <Card
                    title={<Text style={{ fontSize: '18px', color: '#fa8c16', fontWeight: 'bold' }}>
                        🏆 {t('top_profitable_items')}
                    </Text>}
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(250, 140, 22, 0.1)'
                    }}
                >
                    <Row gutter={[16, 16]}>
                        {topProfitableItems.slice(0, 6).map((item, index) => (
                            <Col xs={24} sm={12} lg={8} key={item.product?.id || `item-${index}`}>
                                <Card
                                    size="small"
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #ffd591',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        background: 'linear-gradient(135deg, #fff7e6 0%, #ffecc7 100%)'
                                    }}
                                >
                                    <div style={{ textAlign: 'center', padding: '8px' }}>
                                        <Text strong style={{
                                            fontSize: '14px',
                                            color: '#fa8c16',
                                            display: 'block',
                                            marginBottom: '8px'
                                        }}>
                                            {item.product?.name || 'Unknown Product'}
                                        </Text>

                                        {item.product?.code && (
                                            <Text style={{
                                                fontSize: '12px',
                                                color: '#8c8c8c',
                                                display: 'block',
                                                marginBottom: '12px'
                                            }}>
                                                Code: {item.product.code}
                                            </Text>
                                        )}

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '8px',
                                            fontSize: '12px'
                                        }}>
                                            <div>
                                                <Text style={{ color: '#52c41a' }}>💰 {t('sell_value')}</Text>
                                                <br />
                                                <Text strong>{item.sellValue?.toLocaleString() || '0'}</Text>
                                            </div>
                                            <div>
                                                <Text style={{ color: '#ff4d4f' }}>💸 {t('buy_value')}</Text>
                                                <br />
                                                <Text strong>{item.buyValue?.toLocaleString() || '0'}</Text>
                                            </div>
                                            <div>
                                                <Text style={{ color: '#1890ff' }}>📊 {t('profit')}</Text>
                                                <br />
                                                <Text strong style={{ color: '#52c41a' }}>
                                                    {item.profit?.toLocaleString() || '0'}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text style={{ color: '#722ed1' }}>📈 {t('qty_sold')}</Text>
                                                <br />
                                                <Text strong>{item.qtySold?.toLocaleString() || '0'}</Text>
                                            </div>
                                        </div>

                                        <div style={{
                                            marginTop: '12px',
                                            padding: '8px',
                                            backgroundColor: '#fff',
                                            borderRadius: '6px',
                                            border: '1px solid #ffd591'
                                        }}>
                                            <Text style={{
                                                color: item.profitMargin >= 0 ? '#52c41a' : '#ff4d4f',
                                                fontWeight: 'bold',
                                                fontSize: '14px'
                                            }}>
                                                {item.profitMargin?.toFixed(2) || '0.00'}% Margin
                                            </Text>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Data Table */}
            <Card
                title={<Text style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }}>
                    📈 {t('profit_breakdown_by_currency')}
                </Text>}
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="currency"
                        pagination={false}
                        scroll={{ x: true }}
                        size="middle"
                        bordered
                        style={{
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                        rowClassName={(record, index) =>
                            index % 2 === 0 ? 'even-row' : 'odd-row'
                        }
                        locale={{
                            emptyText: !loading && data.length === 0 ?
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                                    <Text type="secondary" style={{ fontSize: '16px' }}>
                                        {t('no_data_available')}
                                    </Text>
                                </div> : undefined
                        }}
                    />
                </Spin>
            </Card>

            {/* Enhanced Empty State Message */}
            {!loading && data.length === 0 && (!totalMetrics || !totalMetrics.byCurrency || Object.keys(totalMetrics.byCurrency).length === 0) && (
                <Card style={{
                    marginTop: '16px',
                    textAlign: 'center',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '2px dashed #d9d9d9'
                }}>
                    <div style={{ padding: '40px' }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>📊</div>
                        <Title level={4} style={{ color: '#8c8c8c', marginBottom: '12px' }}>
                            {t('no_profit_data_for_period')}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '16px' }}>
                            {t('try_different_date_range')}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
                            💡 Try selecting a wider date range or check if there are any transactions in this period
                        </Text>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ProfitReport;
